import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { X, Sparkles, AlertCircle, Clock, Flame } from 'lucide-react';
import IgnitingLoader from './IgnitingLoader';
import { exercises, getExerciseById } from '../data/exercises';
import { localISO } from '../data/programs';
import { useProfile } from '../context/ProfileContext';
import { useAuth, formatApiError } from '../context/AuthContext';

const AI_CONSENT_REMINDER_KEY = 'enferno_ai_consent_shown_v1';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Day-type picker. `slug` MUST match the keys in the backend DAY_TYPE_FILTERS map
// (llm_service.py) — that's what scopes the catalog the model sees. `focus` is the
// human label sent as the prompt's FOCUS line.
const DAY_TYPES = [
  { slug: 'push', label: 'Push', hint: 'Chest / Delt / Tri', focus: 'Push Day (chest, shoulders & triceps)' },
  { slug: 'pull', label: 'Pull', hint: 'Back / Bi', focus: 'Pull Day (back & biceps)' },
  { slug: 'legs', label: 'Legs', focus: 'Leg Day' },
  { slug: 'arms', label: 'Arms', focus: 'Arm Day' },
  { slug: 'shoulders', label: 'Shoulders', focus: 'Shoulder Day' },
  { slug: 'core', label: 'Core', focus: 'Core Day' },
  { slug: 'upper', label: 'Upper', focus: 'Upper Body Day' },
  { slug: 'lower', label: 'Lower', focus: 'Lower Body Day' },
  { slug: 'full-body', label: 'Full Body', focus: 'Full Body' },
  { slug: 'stretching', label: 'Stretching', focus: 'Stretching & Mobility' },
];

const dayTypeFocus = (slug, fallback) =>
  (DAY_TYPES.find((d) => d.slug === slug) || {}).focus || fallback || 'Full Body';

/**
 * AIWorkoutModal — Triggered from "Ignite a custom workout" buttons.
 * Calls /api/ai/workouts with the user's profile + exercise catalog + focus,
 * displays the generated workout, lets the user apply it as today's session.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   focus: e.g. "Push Day", "Quad Day"
 *   onApply: (workout) => void — receives the generated workout to use
 *   excludeIds: number[] — exercise ids already in today's scheduled plan; the AI
 *     is an ALTERNATIVE session, so these are withheld from it to force variety
 *   defaultDayType: slug — pre-selects the picker to today's scheduled focus
 */
const AIWorkoutModal = ({ open, onClose, focus, onApply, excludeIds, defaultDayType }) => {
  const { profile, history } = useProfile();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [workout, setWorkout] = useState(null);
  const [error, setError] = useState('');
  const [usage, setUsage] = useState(null);
  const [dayType, setDayType] = useState(defaultDayType || 'full-body');
  const [needsConsentNotice, setNeedsConsentNotice] = useState(() => {
    try { return !localStorage.getItem(AI_CONSENT_REMINDER_KEY); } catch { return false; }
  });

  const loadUsage = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ai/usage`);
      setUsage(data);
    } catch { /* non-critical — meter just stays hidden */ }
  }, []);

  const dismissConsent = () => {
    try { localStorage.setItem(AI_CONSENT_REMINDER_KEY, '1'); } catch { /* ignore */ }
    setNeedsConsentNotice(false);
    // Fall through to the day-type picker — no longer auto-generates.
  };

  const generate = async () => {
    setBusy(true);
    setError('');
    setWorkout(null);
    try {
      const recent = (history || []).slice(0, 5).map((h) => h.title).filter(Boolean);
      const minutes = profile.minutesPerSession || 45;
      // User-LOCAL date (same helper that keys painLog / wellbeingLog) so the
      // backend resolves "today's" check-ins correctly regardless of server TZ.
      const clientDate = localISO();
      const { data } = await axios.post(`${API}/ai/workouts`, {
        focus: dayTypeFocus(dayType, focus),
        minutes,
        catalog: exercises,
        recent_titles: recent,
        client_date: clientDate,
        exclude_ids: excludeIds || [],
        day_type: dayType,
      });
      setWorkout(data.workout);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  // Fetch usage on open so the caption is ready before / after generation.
  useEffect(() => {
    if (open && user) loadUsage();
  }, [open, user, loadUsage]);

  // On open, reset to a fresh picker and default the selection to the caller's
  // scheduled focus so the common case is a single confirming tap. (No auto-generate:
  // the user picks a day type first, then ignites.)
  useEffect(() => {
    if (open) {
      setWorkout(null);
      setError('');
      if (defaultDayType) setDayType(defaultDayType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Usage caption: always shown for trial users; shown for paid users only at ≥70% of monthly limit.
  const usageCaption = (() => {
    if (!usage || usage.is_admin || usage.workouts_limit == null) return null;
    const remaining = Math.max(0, usage.workouts_limit - usage.workouts_used);
    const isTrial = usage.workout_period === 'trial';
    const nearCap = usage.workouts_used / usage.workouts_limit >= 0.7;
    if (!isTrial && !nearCap) return null;
    if (remaining === 0) {
      return isTrial
        ? 'Trial AI workouts complete — your saved ones are yours to keep. Enferno keeps the coach adapting to your check-ins.'
        : 'Fresh AI workouts arrive at the start of next month — your saved workouts and full library are open in the meantime.';
    }
    return isTrial
      ? `${remaining} of ${usage.workouts_limit} free trial AI workout${remaining === 1 ? '' : 's'} remaining.`
      : `${remaining} AI workout${remaining === 1 ? '' : 's'} left this month.`;
  })();

  // Conversion affordance: only the trial-exhausted caption gets a real link to plans.
  const trialExhausted = !!usage && !usage.is_admin && usage.workouts_limit != null
    && usage.workout_period === 'trial' && (usage.workouts_limit - usage.workouts_used) <= 0;

  if (!open) return null;

  if (!user) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
        <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6 text-center">
          <Sparkles className="w-8 h-8 text-enf-accent-fg mx-auto mb-3" />
          <div className="font-display text-3xl tracking-wide mb-2">Sign in required</div>
          <p className="text-enf-text-secondary text-sm mb-4">AI-generated workouts are an Enferno feature for signed-in athletes.</p>
          <button onClick={onClose} className="px-4 py-2 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-enf-surface-deep border border-enf-accent-fg/40 rounded-xl"
        data-testid="ai-workout-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-enf-surface-deep border-b border-enf-surface-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-enf-accent flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-enf-text-primary" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-enf-accent-fg font-bold">AI generated</div>
              <div className="text-sm font-semibold">{dayTypeFocus(dayType, focus)} · {profile.minutesPerSession || 45} min</div>
            </div>
          </div>
          <button onClick={onClose} className="text-enf-text-muted hover:text-enf-text-primary p-1" aria-label="Close" data-testid="ai-workout-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {needsConsentNotice && !busy && !workout && !error && (
            <div className="text-center py-4">
              <Sparkles className="w-8 h-8 text-enf-accent-fg mx-auto mb-3" />
              <div className="text-xs font-semibold uppercase tracking-widest text-enf-text-secondary mb-2">AI Data Notice</div>
              <h3 className="font-display text-2xl tracking-wide mb-3">Your profile powers the AI</h3>
              <p className="text-sm text-enf-text-secondary leading-relaxed mb-6 max-w-sm mx-auto">
                Enferno AI uses your training profile — experience, goals, equipment, and injury flags — to personalize this workout. You can review or change this in{' '}
                <Link to="/profile" className="text-enf-role-info hover:underline" onClick={onClose}>
                  Profile → Your Data &amp; Consents
                </Link>.
              </p>
              <button
                type="button"
                onClick={dismissConsent}
                className="px-6 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors"
                data-testid="ai-workout-consent-got-it"
              >
                Got it — choose my day
              </button>
            </div>
          )}

          {/* Day-type picker — shown once consent is handled and before a result exists. */}
          {!needsConsentNotice && !busy && !workout && !error && (
            <div data-testid="ai-workout-picker">
              <div className="text-xs font-semibold uppercase tracking-widest text-enf-text-secondary mb-1">What are we training?</div>
              <p className="text-sm text-enf-text-secondary mb-4">Pick your day — the coach builds a fresh session for it and skips what's already on today's plan.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
                {DAY_TYPES.map((dt) => {
                  const active = dt.slug === dayType;
                  return (
                    <button
                      key={dt.slug}
                      type="button"
                      onClick={() => setDayType(dt.slug)}
                      className={`px-3 py-2.5 rounded-md border text-sm font-medium text-left transition-colors ${active ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary' : 'bg-enf-surface-mid border-enf-surface-border hover:bg-enf-surface-elevated text-enf-text-primary'}`}
                      data-testid={`ai-day-${dt.slug}`}
                      aria-pressed={active}
                    >
                      <div>{dt.label}</div>
                      {dt.hint && <div className={`text-[10px] ${active ? 'text-enf-text-primary/80' : 'text-enf-text-muted'}`}>{dt.hint}</div>}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={generate}
                className="w-full px-4 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                data-testid="ai-workout-generate"
              >
                <Flame className="w-4 h-4 scale-x-[-1]" /> Ignite my {dayTypeFocus(dayType, focus)}
              </button>
            </div>
          )}

          {busy && (
            <IgnitingLoader label="Igniting your workout..." />
          )}

          {error && !busy && (
            <div className="px-4 py-3 bg-enf-role-danger/10 border border-enf-role-danger/40 rounded-md text-sm text-enf-role-danger flex items-start gap-2" data-testid="ai-workout-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold mb-0.5">The coach is taking a breath</div>
                <div className="text-enf-text-secondary">{error}</div>
                <button onClick={generate} className="text-xs text-enf-role-info hover:text-enf-text-primary mt-2">Light it again</button>
              </div>
            </div>
          )}

          {workout && !busy && (
            <div data-testid="ai-workout-content">
              <div className="font-display text-4xl tracking-wide leading-tight mb-2">{workout.title}</div>
              {workout.focus && <div className="text-sm text-enf-text-secondary mb-3">{workout.focus}</div>}
              {workout.coach_intro && (
                <div className="bg-enf-accent/5 border border-enf-accent-fg/20 rounded-md p-3 mb-3 text-sm text-enf-text-primary italic leading-relaxed">
                  "{workout.coach_intro}"
                </div>
              )}
              {workout.why_this_workout && (
                <details className="mb-5 group" data-testid="ai-workout-reasoning">
                  <summary className="cursor-pointer list-none flex items-center gap-2 text-xs uppercase tracking-widest text-enf-role-info font-semibold hover:text-enf-text-primary transition-colors">
                    <span className="inline-block w-3.5 h-3.5 rounded-full border border-enf-role-info/50 flex items-center justify-center text-[10px] group-open:rotate-45 transition-transform">?</span>
                    Why this workout?
                  </summary>
                  <div className="mt-2 p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md text-sm text-enf-text-secondary leading-relaxed">
                    {workout.why_this_workout}
                  </div>
                </details>
              )}

              <div className="space-y-3 mb-5">
                {(workout.blocks || []).map((b, i) => {
                  const ex = getExerciseById(b.ref_id);
                  return (
                    <div key={i} className="flex gap-3 p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md" data-testid={`ai-workout-block-${i}`}>
                      <div className="w-7 h-7 rounded bg-enf-accent/15 text-enf-accent-fg flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm">{ex?.name || b.ref_id}</div>
                        <div className="text-xs text-enf-text-secondary mt-0.5">
                          <span className="font-bold text-enf-text-primary">{b.sets} × {b.reps}</span>
                          <span className="text-enf-text-muted mx-1.5">·</span>
                          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {b.rest_seconds}s rest</span>
                        </div>
                        {b.cue && (
                          <div className="text-xs text-enf-role-info mt-1.5 flex gap-1.5">
                            <Flame className="w-3 h-3 mt-0.5 flex-shrink-0 scale-x-[-1]" /> {b.cue}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {(() => {
                const flagged = workout._excluded_for_flags || [];
                const stripped = workout._sanitized || [];
                const total = flagged.length + stripped.length;
                if (!total) return null;
                return (
                  <details className="mb-5 group" data-testid="ai-workout-removed">
                    <summary className="cursor-pointer list-none flex items-center gap-2 text-xs uppercase tracking-widest text-enf-text-muted font-semibold hover:text-enf-text-primary transition-colors">
                      <span className="inline-block w-3.5 h-3.5 rounded-full border border-enf-text-muted/50 flex items-center justify-center text-[10px] leading-none group-open:rotate-45 transition-transform">+</span>
                      {total} hidden for your flagged areas
                    </summary>
                    <div className="mt-2 p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md">
                      <p className="text-xs text-enf-text-secondary mb-2 leading-relaxed">
                        These aren't in today's session because they load a body part you've flagged — so a safe day can look shorter than a full library day. Nothing was dropped silently. Change your flags in{' '}
                        <Link to="/profile" className="text-enf-role-info hover:underline" onClick={onClose}>Profile</Link>{' '}to see them again.
                      </p>
                      <ul className="space-y-1">
                        {flagged.map((e, i) => (
                          <li key={`f${i}`} className="text-xs flex items-center justify-between gap-2">
                            <span className="text-enf-text-primary">{e.name}</span>
                            <span className="text-enf-text-muted capitalize">{(e.areas || []).join(', ')}</span>
                          </li>
                        ))}
                        {stripped.map((e, i) => {
                          const ex = getExerciseById(e.ref_id);
                          return (
                            <li key={`s${i}`} className="text-xs flex items-center justify-between gap-2">
                              <span className="text-enf-text-primary">{ex?.name || e.ref_id}</span>
                              <span className="text-enf-text-muted">{e.reason === 'contraindicated' ? 'flagged area' : 'not in catalog'}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </details>
                );
              })()}

              <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-enf-surface-border">
                <button onClick={onClose}
                  className="flex-1 px-4 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-medium transition-colors"
                  data-testid="ai-workout-close-blueprint"
                >
                  Close · keep my current workout
                </button>
                {onApply && (
                  <button onClick={() => { onApply(workout); onClose(); }}
                    className="flex-1 px-4 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                    data-testid="ai-workout-apply"
                  >
                    <Flame className="w-4 h-4 scale-x-[-1]" /> Light it up · use today
                  </button>
                )}
                <button onClick={generate}
                  className="px-4 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  title="Regenerate the same day type"
                  data-testid="ai-workout-regenerate"
                >
                  <Sparkles className="w-4 h-4" /> Regenerate
                </button>
                <button onClick={() => { setWorkout(null); setError(''); }}
                  className="px-4 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  title="Pick a different day type"
                  data-testid="ai-workout-change-day"
                >
                  Change day
                </button>
              </div>
            </div>
          )}
          {usageCaption && (
            <p className="mt-4 text-center text-[11px] text-enf-text-muted">
              {usageCaption}
              {trialExhausted && (
                <>{' '}<Link to="/pricing" onClick={onClose} className="text-enf-role-info hover:underline font-medium">See plans</Link></>
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIWorkoutModal;
